import { Modal } from '@/components/ui/Modal';
import type { SaveSettings } from '@/state/playerStore';
import { type ChangeEvent, useEffect, useState } from 'react';

const RESET_TOAST_MS = 2500;

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  settings: SaveSettings;
  reducedMotion: boolean;
  onChangeMasterVolume: (value: number) => void;
  onToggleMute: (muted: boolean) => void;
  onToggleVibration: (enabled: boolean) => void;
  onResetAllProgress: () => void;
}

export function SettingsModal({
  open,
  onClose,
  settings,
  reducedMotion,
  onChangeMasterVolume,
  onToggleMute,
  onToggleVibration,
  onResetAllProgress,
}: SettingsModalProps) {
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetToast, setResetToast] = useState(false);

  useEffect(() => {
    if (!resetToast) return;
    const id = window.setTimeout(() => setResetToast(false), RESET_TOAST_MS);
    return () => window.clearTimeout(id);
  }, [resetToast]);

  const handleClose = () => {
    setConfirmReset(false);
    setResetToast(false);
    onClose();
  };

  const handleVolume = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeMasterVolume(Number(event.target.value) / 100);
  };

  const volumePct = Math.round(settings.masterVolume * 100);

  return (
    <Modal open={open} onClose={handleClose} title="Settings">
      <ul className="settings-list">
        <li className="settings-row">
          <label htmlFor="settings-volume" className="settings-label">
            Master volume
          </label>
          <input
            id="settings-volume"
            type="range"
            min={0}
            max={100}
            step={1}
            value={volumePct}
            onChange={handleVolume}
            aria-valuetext={`${volumePct} percent`}
          />
          <span className="settings-value" aria-hidden="true">
            {volumePct}%
          </span>
        </li>
        <li className="settings-row">
          <label htmlFor="settings-mute" className="settings-label">
            Mute
          </label>
          <input
            id="settings-mute"
            type="checkbox"
            role="switch"
            checked={settings.muted}
            aria-checked={settings.muted}
            onChange={(e) => onToggleMute(e.target.checked)}
          />
        </li>
        <li className="settings-row">
          <label htmlFor="settings-vibration" className="settings-label">
            Vibration
          </label>
          <input
            id="settings-vibration"
            type="checkbox"
            role="switch"
            checked={settings.vibrationEnabled}
            aria-checked={settings.vibrationEnabled}
            onChange={(e) => onToggleVibration(e.target.checked)}
          />
        </li>
        <li className="settings-row settings-row--info">
          <span className="settings-label">Reduced motion</span>
          <span className="settings-value">
            {reducedMotion ? 'On (auto-detected)' : 'Off (auto-detected)'}
          </span>
        </li>
      </ul>

      <div className="settings-reset">
        {confirmReset ? (
          <div className="settings-reset__confirm" role="alert">
            <p>Erase all progress, currency, and personal best?</p>
            <div className="settings-reset__actions">
              <button type="button" className="modal-btn" onClick={() => setConfirmReset(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="modal-btn modal-btn--danger"
                onClick={() => {
                  setConfirmReset(false);
                  onResetAllProgress();
                  setResetToast(true);
                }}
              >
                Reset everything
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="modal-btn modal-btn--danger"
            onClick={() => setConfirmReset(true)}
          >
            Reset all progress
          </button>
        )}
      </div>
      {resetToast && (
        <output className="settings-toast" aria-live="polite">
          Progress reset
        </output>
      )}
    </Modal>
  );
}
